
import { HttpEvent, HttpEventType, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { catchError, last, tap } from 'rxjs/operators';

import { HttpResponseService } from '../_services/http-response.service';
import { HttpErrorHandlingService } from '../_services/http-error-handling.service';
import { SessionExpiredError, SILENT_AUTH_REQUEST } from '../_services/auth/auth-request-context';

// https://github.com/angular/angular/issues/18224
@Injectable()
export class ConnectivityInterceptor implements HttpInterceptor {

    private readonly interestingMethods = ['post', 'put', 'delete'];
    private readonly interestingHttpEventTypes = [HttpEventType.Response];

    constructor(
        private readonly httpResponseService: HttpResponseService,
        private readonly errorHandling: HttpErrorHandlingService) { }

    public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {

        const configuredErrorMode = this.errorHandling.consume(request.url);
        // Token-refresh failures are handled by AuthService (retry or logout), not a generic toast.
        const errorMode = configuredErrorMode === 'Block' && /\/auth\/token\/refresh\/?(?:\?|$)/.test(request.url)
            ? 'HideToastr'
            : configuredErrorMode;

        return next.handle(request)
            .pipe(
                // We only want the last emitted value. It should be the "complete"
                last(),
                // Handle the response.
                tap(event => {
                    // Filter out only things we're interested in (POST, PUT, DELETE, etc...)
                    if (this.isInteresting(request, event)) {
                        this.httpResponseService.handleResponse(event);
                    }
                }),
                // If there's an error, let's catch it, handle it, then throw it up the chain.
                catchError((err: any) => {
                    if (err instanceof SessionExpiredError || request.context?.get(SILENT_AUTH_REQUEST)) {
                        throw err;
                    }
                    if (errorMode === 'Propagate') {
                        throw err;
                    }

                    if (errorMode !== 'HideToastr') {
                        this.httpResponseService.handleResponse(err);
                    }

                    // return of({ innerError: err, handled: true });
                    throw { innerError: err, handled: true };
                })
            );
    }

    private isInteresting(request: HttpRequest<any>, response: HttpEvent<any>): boolean {

        const isInterstingMethod = this.interestingMethods.any(p => String.looseEquals(p, request.method));
        const isInterestingType = this.interestingHttpEventTypes.any(p => p === response.type);

        return isInterstingMethod && isInterestingType && !this.isSilentRequest(request);
    }

    private isSilentRequest(request: HttpRequest<any>): boolean {
        // Authentication and permission refresh calls are internal maintenance calls.
        // Users should not see generic success toasts for either request.
        return request.url.indexOf('/auth/token/refresh') >= 0
            || request.url.indexOf('/auth/refresh') >= 0;
    }
}
